import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { KeyIcon, BuildingIcon, UserIcon, AlertCircleIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
export const LoginPage = () => {
  const [loginType, setLoginType] = useState<'admin' | 'dealer'>('admin');
  const [formData, setFormData] = useState({
    dealerCode: '',
    username: '',
    password: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const {
    login
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Get the return path from location state or default to the appropriate dashboard
  const from = location.state?.from?.pathname || (loginType === 'admin' ? '/admin' : '/dealer');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      // For demo, we'll use hardcoded credentials:
      // Admin: username: admin, password: admin123
      // Dealer: username: dealer, password: dealer123
      const success = await login(formData.username, formData.password, loginType);
      if (success) {
        navigate(from, {
          replace: true
        });
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } catch (err) {
      setError('An error occurred during login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-id="element-2865">
      <div className="sm:mx-auto sm:w-full sm:max-w-md" data-id="element-2866">
        <Link to="/" className="flex justify-center mb-6" data-id="element-2867">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tpKLaYzfGrRLG8howg5M89/sellup-logo.png" alt="SellUp Logo" className="h-8" data-id="element-2868" />
        </Link>
        <h2 className="text-center text-3xl font-bold text-gray-900" data-id="element-2869">
          {loginType === 'admin' ? 'Admin Login' : 'Dealer Login'}
        </h2>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md" data-id="element-2870">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10" data-id="element-2871">
          <div className="flex justify-center space-x-4 mb-8" data-id="element-2872">
            <button className={`px-4 py-2 rounded-full flex items-center space-x-2 ${loginType === 'admin' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`} onClick={() => setLoginType('admin')} data-id="element-2873">
              <UserIcon className="h-4 w-4" data-id="element-2874" />
              <span data-id="element-2875">Admin</span>
            </button>
            <button className={`px-4 py-2 rounded-full flex items-center space-x-2 ${loginType === 'dealer' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`} onClick={() => setLoginType('dealer')} data-id="element-2876">
              <BuildingIcon className="h-4 w-4" data-id="element-2877" />
              <span data-id="element-2878">Dealer</span>
            </button>
          </div>
          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start" data-id="element-2879">
              <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2880" />
              <span className="text-red-700 text-sm" data-id="element-2881">{error}</span>
            </div>}
          <form className="space-y-6" onSubmit={handleSubmit} data-id="element-2882">
            {loginType === 'dealer' && <div data-id="element-2883">
                <label htmlFor="dealerCode" className="block text-sm font-medium text-gray-700" data-id="element-2884">
                  Dealer Code
                </label>
                <div className="mt-1" data-id="element-2885">
                  <input id="dealerCode" name="dealerCode" type="text" required={loginType === 'dealer'} value={formData.dealerCode} onChange={e => setFormData({
                ...formData,
                dealerCode: e.target.value
              })} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-2886" />
                </div>
              </div>}
            <div data-id="element-2887">
              <label htmlFor="username" className="block text-sm font-medium text-gray-700" data-id="element-2888">
                Username
              </label>
              <div className="mt-1" data-id="element-2889">
                <input id="username" name="username" type="text" required value={formData.username} onChange={e => setFormData({
                ...formData,
                username: e.target.value
              })} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder={loginType === 'admin' ? 'admin' : 'dealer'} data-id="element-2890" />
              </div>
            </div>
            <div data-id="element-2891">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700" data-id="element-2892">
                Password
              </label>
              <div className="mt-1" data-id="element-2893">
                <input id="password" name="password" type="password" required value={formData.password} onChange={e => setFormData({
                ...formData,
                password: e.target.value
              })} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder={loginType === 'admin' ? 'admin123' : 'dealer123'} data-id="element-2894" />
              </div>
            </div>
            <div className="flex items-center justify-between" data-id="element-2895">
              <div className="flex items-center" data-id="element-2896">
                <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2897" />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900" data-id="element-2898">
                  Remember me
                </label>
              </div>
              <div className="text-sm" data-id="element-2899">
                <a href="#" className="font-medium text-blue-600 hover:text-blue-500" data-id="element-2900">
                  Forgot your password?
                </a>
              </div>
            </div>
            <div data-id="element-2901">
              <button type="submit" className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" disabled={isLoading} data-id="element-2902">
                <KeyIcon className="h-4 w-4 mr-2" data-id="element-2903" />
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
            <div className="text-center text-sm text-gray-500 mt-4" data-id="element-2904">
              <p data-id="element-2905">
                Demo credentials: <br data-id="element-2906" />
                <span className="font-medium" data-id="element-2907">
                  {loginType === 'admin' ? 'admin / admin123' : 'dealer / dealer123'}
                </span>
              </p>
            </div>
          </form>
          {loginType === 'dealer' && <div className="mt-6" data-id="element-2908">
              <div className="relative" data-id="element-2909">
                <div className="absolute inset-0 flex items-center" data-id="element-2910">
                  <div className="w-full border-t border-gray-300" data-id="element-2911" />
                </div>
                <div className="relative flex justify-center text-sm" data-id="element-2912">
                  <span className="px-2 bg-white text-gray-500" data-id="element-2913">
                    New to SellUp?
                  </span>
                </div>
              </div>
              <div className="mt-6" data-id="element-2914">
                <a href="#" className="w-full flex justify-center py-2 px-4 border border-blue-600 rounded-md shadow-sm text-sm font-medium text-blue-600 bg-white hover:bg-blue-50" data-id="element-2915">
                  Register as a Dealer
                </a>
              </div>
            </div>}
        </div>
      </div>
    </div>;
};